import { markRequest } from '@/lib/actionFailureSignals';
import type { LaborScheduleShift } from './laborCalendar';
import type { SupabaseClient } from '@supabase/supabase-js';

export const BREAK_CLASSIFICATIONS = ['lunch', 'rest', 'walk_away', 'none'] as const;
export type BreakClassification = typeof BREAK_CLASSIFICATIONS[number];
export type UnpaidBreak = { id: string; minutes: number; classification: BreakClassification };
export type DailyBreak = {
  shift_id: string; profile_id: string | null; resource_id: string; work_date: string; minutes: number;
  owner_shift_id: string | null; waived: boolean; classification: BreakClassification;
};
export type ShiftBreakDraft = {
  unpaidBreaks: UnpaidBreak[]; lunchBreakMinutes: number | null; lunchBreakClassification: BreakClassification;
};

export function breakClassification(value: unknown): BreakClassification {
  return BREAK_CLASSIFICATIONS.includes(value as BreakClassification) ? value as BreakClassification : 'none';
}

export function normalizeUnpaidBreaks(value: unknown): UnpaidBreak[] | null {
  if (!Array.isArray(value)) return null;
  return value.map(row => ({ id: String(row.id), minutes: Number(row.minutes), classification: breakClassification(row.classification) }));
}

/** Unclassified historical minutes stay separate from the inherited daily lunch. */
export function additionalShiftBreaks(shift: LaborScheduleShift): UnpaidBreak[] {
  if (shift.unpaidBreaks != null) return shift.unpaidBreaks;
  const minutes = shift.unpaidBreakMinutes ?? shift.slingUnpaidBreakMinutes ?? 0;
  return minutes > 0 ? [{ id: shift.recurrenceSeriesId || shift.id, minutes, classification: 'none' }] : [];
}

export function unpaidBreakLabel(row: Pick<UnpaidBreak, 'minutes' | 'classification'>): string {
  const kind = row.classification === 'none' ? '' : `${row.classification.replace('_', ' ')} `;
  return `${row.minutes}m ${kind}break`;
}

export function shiftBreakLabel(shift: LaborScheduleShift, resourceId?: string | null): string {
  const rows: Array<Pick<UnpaidBreak, 'minutes' | 'classification'>> = [...additionalShiftBreaks(shift)];
  const assigned = shift.assignments.some(row => ['assigned', 'accepted', 'offered'].includes(row.status));
  if (!assigned) {
    const minutes = shift.lunchBreakMinutes ?? 60;
    if (minutes > 0) rows.unshift({ minutes, classification: shift.lunchBreakClassification ?? 'lunch' });
  } else {
    const seen = new Set<string>();
    for (const row of shift.dailyBreaks ?? []) {
      if (row.owner_shift_id !== shift.id || row.minutes <= 0 || (resourceId && /^(employee|freelancer):/.test(resourceId) && row.resource_id !== resourceId)) continue;
      const key = `${row.work_date}:${row.minutes}:${row.classification}`;
      if (!seen.has(key)) rows.unshift({ minutes: row.minutes, classification: row.classification });
      seen.add(key);
    }
  }
  return rows.map(unpaidBreakLabel).join(' · ');
}

/** JSON snapshot avoids the Data API's row cap; each call is bounded to 500 shifts/45 days. */
export async function hydrateShiftBreaks(shifts: LaborScheduleShift[], start: string, end: string, client: SupabaseClient): Promise<LaborScheduleShift[]> {
  const persisted = shifts.filter(row => !row.openActual && /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(row.id));
  if (!persisted.length) return shifts;
  const rows: DailyBreak[] = [];
  const chunkMs = 30 * 24 * 60 * 60 * 1000;
  for (let cursor = Date.parse(start); cursor < Date.parse(end); cursor += chunkMs) {
    const until = Math.min(cursor + chunkMs, Date.parse(end));
    const ids = persisted.filter(row => Date.parse(row.startsAt) < until && (row.endsAt ? Date.parse(row.endsAt) : Date.parse(row.startsAt) + 60 * 60 * 1000) > cursor).map(row => row.id);
    for (let offset = 0; offset < ids.length; offset += 500) {
      const { data, error } = await markRequest(client.rpc('labor_schedule_break_summary', {
        p_start: new Date(cursor).toISOString(), p_end: new Date(until).toISOString(), p_shift_ids: ids.slice(offset, offset + 500),
      }), 'read');
      if (error) throw error;
      if (data?.complete !== true || !Array.isArray(data.rows)) throw new Error('Unpaid break details did not load completely. Retry the schedule.');
      rows.push(...data.rows.map((row: DailyBreak) => ({ ...row, minutes: Number(row.minutes), classification: breakClassification(row.classification) })));
    }
  }
  const byShift = new Map<string, Map<string, DailyBreak>>();
  for (const row of rows) {
    if (!byShift.has(row.shift_id)) byShift.set(row.shift_id, new Map());
    byShift.get(row.shift_id)!.set(`${row.resource_id}:${row.work_date}`, row);
  }
  return shifts.map(shift => ({ ...shift, dailyBreaks: [...(byShift.get(shift.id)?.values() ?? [])] }));
}
